// Single source of truth for "can I add this column as another trend
// series?" -- used by both the in-tool `+ Compare` picker (CompareMenu)
// and the grid's row context menu (Pattern B `Add to current trend`).
//
// v1 rules (intentionally narrow; loosen later if needed):
//   1. Must be a series-eligible material column (has a *-series
//      counterpart in CALC_SOURCES).
//   2. Must share `measureKind` with the primary series so we can keep
//      a single Y axis (no dual-axis layouts yet).
//   3. Must not already be on the chart (dedupe by `columnInstance.id`).
//   4. Must not push the series count over `max` (default 4: primary +
//      3 compares).
//
// Returns `{ ok, reason }`. The reason string is what the picker's
// disabled tooltip surfaces to the user, so phrase it for end-users.

import { COLUMN_CATALOG_MAP, getSeriesCounterpartSource } from '../../../lib/constants'

export const DEFAULT_MAX_SERIES = 4

function getCatalog (columnInstance) {
  if (!columnInstance) return null
  return COLUMN_CATALOG_MAP[columnInstance.columnType] || null
}

function getMeasureKind (columnInstance, columnDefinitions) {
  if (!columnInstance) return null
  const def = (columnDefinitions || []).find(d => d.accessorKey === columnInstance.id)
  if (def?.measureKind) return def.measureKind
  // Fallback to the catalog entry -- column definitions are produced
  // from the same catalog, but the catalog is authoritative when the
  // grid's columnDefinitions hasn't synced yet (e.g. tool opened mid
  // re-render).
  const catalog = getCatalog(columnInstance)
  return catalog?.measureKind || null
}

export function isSeriesEligible (columnInstance) {
  const catalog = getCatalog(columnInstance)
  if (!catalog) return false
  if (catalog.kind && catalog.kind !== 'material') return false
  return !!getSeriesCounterpartSource(catalog.calcSource, columnInstance.options)
}

/**
 * @param {object} args
 * @param {object} args.primary -- the primary series's columnInstance
 * @param {Array<{ columnInstance }>} args.currentSeries -- series already on the chart
 * @param {object} args.candidate -- the columnInstance being considered
 * @param {Array} [args.columnDefinitions] -- the grid's columnDefinitions (for measureKind lookup)
 * @param {number} [args.max] -- series cap (default 4)
 * @returns {{ ok: boolean, reason: string|null }}
 */
export function canAddSeriesCandidate ({
  primary,
  currentSeries = [],
  candidate,
  columnDefinitions = [],
  max = DEFAULT_MAX_SERIES
}) {
  if (!candidate) {
    return { ok: false, reason: 'No measure selected' }
  }
  if (!primary) {
    // Without a primary there's nothing to compare against; the picker
    // shouldn't render at all in this case, but guard so callers don't
    // crash on transient empty state.
    return { ok: false, reason: 'No primary series' }
  }
  if (currentSeries.length >= max) {
    return { ok: false, reason: `Up to ${max} series at a time` }
  }
  if (currentSeries.some(s => s.columnInstance?.id === candidate.id)) {
    return { ok: false, reason: 'Already on chart' }
  }
  if (!isSeriesEligible(candidate)) {
    return { ok: false, reason: 'No trend available' }
  }
  const primaryKind = getMeasureKind(primary, columnDefinitions)
  const candidateKind = getMeasureKind(candidate, columnDefinitions)
  if (primaryKind && candidateKind && primaryKind !== candidateKind) {
    return { ok: false, reason: 'Different measure kind' }
  }
  return { ok: true, reason: null }
}
